import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from './ThemeContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const GAME_MODES = { FINITE: 'finite', INFINITE: 'infinite' };
const TIME_HORIZONS = [10, 20, 30];

const CreateRoom = () => {
  const [activeTab, setActiveTab] = useState('join');
  const [roomName, setRoomName] = useState('');
  const [joinRoomName, setJoinRoomName] = useState('');
  const [gameMode, setGameMode] = useState(GAME_MODES.FINITE);
  const [timeHorizon, setTimeHorizon] = useState(10);
  const [createError, setCreateError] = useState('');
  const [joinError, setJoinError] = useState('');
  const navigate = useNavigate();
  const { theme, isAscii: isAnyAscii } = useTheme();
  const isDark = theme === 'dark';
  const isAscii = isAnyAscii && isDark;
  const isAsciiLight = isAnyAscii && !isDark;

  const handleCreateRoom = async () => {
    if (!roomName) { setCreateError('Please provide a room name.'); return; }
    try {
      const response = await axios.post(
        `${backendUrl}/create-room/`,
        { name: roomName, game_mode: gameMode, time_horizon: gameMode === GAME_MODES.FINITE ? timeHorizon : null },
        { headers: { 'Content-Type': 'application/json', withCredentials: false } }
      );
      navigate('/username-selection', {
        state: {
          roomId: response.data.id,
          roomName: response.data.room_name,
          isCreator: true,
          gameMode: response.data.game_mode,
          timeHorizon: response.data.time_horizon ?? 10,
        },
      });
    } catch (err) {
      setCreateError('Error creating the room. Please try again.');
    }
  };

  const handleJoinRoom = async () => {
    if (!joinRoomName) { setJoinError('Please provide a room name.'); return; }
    try {
      const response = await axios.post(`${backendUrl}/join-room/`, { name: joinRoomName });
      navigate('/username-selection', {
        state: {
          roomId: response.data.id,
          roomName: response.data.room_name,
          isCreator: false,
          gameMode: response.data.game_mode,
          timeHorizon: response.data.time_horizon ?? 10,
        },
      });
    } catch (err) {
      setJoinError(err.response?.data?.detail || 'Error joining the room. Please try again.');
    }
  };

  const s = isAscii ? asciiStyles : isAsciiLight ? asciiLightStyles : isDark ? darkStyles : lightStyles;

  return (
    <div style={s.container}>
      <div style={s.backRow}>
        <button onClick={() => navigate('/')} style={s.backButton}>← Back</button>
      </div>

      {/* Tab switcher */}
      <div style={s.tabRow}>
        <button
          style={activeTab === 'join' ? s.tabActive : s.tab}
          onClick={() => setActiveTab('join')}
        >
          Join Room
        </button>
        <button
          style={activeTab === 'create' ? s.tabActive : s.tab}
          onClick={() => setActiveTab('create')}
        >
          Create Room
        </button>
      </div>

      {/* Card */}
      <div style={s.card}>
        {activeTab === 'join' ? (
          <>
            <label style={s.fieldLabel}>Room ID</label>
            <input
              type="text"
              placeholder="e.g. fun123"
              value={joinRoomName}
              onChange={(e) => { setJoinRoomName(e.target.value); setJoinError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleJoinRoom()}
              style={s.input}
              autoFocus
            />
            {joinError && <p style={s.error}>{joinError}</p>}
            <button onClick={handleJoinRoom} style={s.actionButton}>
              Join Room
            </button>
          </>
        ) : (
          <>
            <label style={s.fieldLabel}>Room name</label>
            <input
              type="text"
              placeholder="e.g. fun123"
              value={roomName}
              onChange={(e) => { setRoomName(e.target.value); setCreateError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateRoom()}
              style={s.input}
              autoFocus
            />

            <div style={s.gameModeRow}>
              <span style={s.fieldLabel}>Game mode</span>
              <div style={s.radioGroup}>
                {[GAME_MODES.FINITE, GAME_MODES.INFINITE].map((mode) => (
                  <label key={mode} style={s.radioLabel}>
                    <input
                      type="radio"
                      name="gameMode"
                      value={mode}
                      checked={gameMode === mode}
                      onChange={() => setGameMode(mode)}
                      style={s.radioInput}
                    />
                    {mode === GAME_MODES.FINITE ? 'Fixed clicks' : '1s life'}
                  </label>
                ))}
              </div>
            </div>

            {gameMode === GAME_MODES.FINITE && (
              <div style={s.gameModeRow}>
                <span style={s.fieldLabel}>Time</span>
                <div style={s.toggle}>
                  {TIME_HORIZONS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTimeHorizon(t)}
                      style={timeHorizon === t ? s.toggleBtnActive : s.toggleBtn}
                    >
                      {t}s
                    </button>
                  ))}
                </div>
              </div>
            )}

            {createError && <p style={s.error}>{createError}</p>}

            <button onClick={handleCreateRoom} style={s.actionButton}>
              Create Room
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const lightStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f8f8f4',
    padding: '20px',
  },
  backRow: { width: '360px', marginBottom: '4px' },
  backButton: {
    background: 'none', border: 'none', fontSize: '0.9rem',
    color: '#4A5568', cursor: 'pointer', padding: '0',
  },
  tabRow: {
    display: 'flex',
    width: '360px',
    borderRadius: '12px 12px 0 0',
    overflow: 'hidden',
    border: '1px solid #CBD5E0',
    borderBottom: 'none',
  },
  tab: {
    flex: 1,
    padding: '10px 0',
    background: '#f0f0ea',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: '#4A5568',
  },
  tabActive: {
    flex: 1,
    padding: '10px 0',
    background: 'white',
    border: 'none',
    borderBottom: '2px solid #f9a825',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: '#1a1a1a',
    fontWeight: '600',
  },
  card: {
    display: 'flex', flexDirection: 'column', alignItems: 'stretch',
    width: '360px', background: 'white',
    borderRadius: '0 0 12px 12px',
    border: '1px solid #CBD5E0', borderTop: 'none',
    padding: '24px', gap: '10px',
  },
  fieldLabel: {
    fontSize: '0.8rem', color: '#888', marginBottom: '-4px',
  },
  input: {
    fontSize: '1rem', padding: '10px 12px', borderRadius: '6px',
    border: '1px solid #ccc', width: '100%', boxSizing: 'border-box',
  },
  gameModeRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  radioGroup: { display: 'flex', gap: '16px' },
  radioLabel: {
    display: 'flex', alignItems: 'center', gap: '5px',
    fontSize: '0.9rem', color: '#333', cursor: 'pointer',
  },
  radioInput: { cursor: 'pointer' },
  toggle: {
    display: 'flex', border: '1px solid #CBD5E0', borderRadius: '6px', overflow: 'hidden',
  },
  toggleBtn: {
    flex: 1, padding: '6px 14px', background: 'white', border: 'none',
    cursor: 'pointer', fontSize: '0.9rem', color: '#4A5568',
  },
  toggleBtnActive: {
    flex: 1, padding: '6px 14px', background: '#2196F3', border: 'none',
    cursor: 'pointer', fontSize: '0.9rem', color: 'white', fontWeight: '600',
  },
  actionButton: {
    width: '100%', fontSize: '1rem', padding: '11px 0', borderRadius: '6px',
    border: 'none', backgroundColor: '#f9a825',
    color: 'white', cursor: 'pointer', fontWeight: '600', marginTop: '2px',
  },
  error: { color: '#e53935', margin: '0', fontSize: '0.875rem' },
};

const darkStyles = {
  container: { ...lightStyles.container, background: 'transparent' },
  backRow: lightStyles.backRow,
  backButton: { ...lightStyles.backButton, color: '#8ab4cc' },
  tabRow: {
    ...lightStyles.tabRow,
    border: '1px solid rgba(0, 245, 255, 0.15)',
    borderBottom: 'none',
  },
  tab: {
    ...lightStyles.tab,
    background: 'rgba(4, 7, 18, 0.6)',
    color: '#8ab4cc',
  },
  tabActive: {
    ...lightStyles.tabActive,
    background: 'rgba(8, 12, 26, 0.95)',
    color: '#ddeeff',
    borderBottom: '2px solid rgba(0, 212, 255, 0.7)',
  },
  card: {
    ...lightStyles.card,
    background: 'rgba(8, 12, 26, 0.95)',
    border: '1px solid rgba(0, 245, 255, 0.15)',
    borderTop: 'none',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
  },
  fieldLabel: { ...lightStyles.fieldLabel, color: 'rgba(0, 245, 255, 0.5)' },
  input: {
    ...lightStyles.input,
    background: 'rgba(4, 7, 18, 0.8)',
    border: '1px solid rgba(0, 245, 255, 0.2)',
    color: '#ddeeff',
  },
  gameModeRow: lightStyles.gameModeRow,
  radioGroup: lightStyles.radioGroup,
  radioLabel: { ...lightStyles.radioLabel, color: '#8ab4cc' },
  radioInput: lightStyles.radioInput,
  toggle: { ...lightStyles.toggle, border: '1px solid rgba(0, 245, 255, 0.2)' },
  toggleBtn: { ...lightStyles.toggleBtn, background: 'transparent', color: '#8ab4cc' },
  toggleBtnActive: { ...lightStyles.toggleBtnActive, background: 'rgba(0, 100, 200, 0.5)', color: '#00d4ff' },
  actionButton: {
    ...lightStyles.actionButton,
    background: 'transparent',
    border: '1px solid rgba(0, 180, 255, 0.5)',
    color: '#00d4ff',
    boxShadow: '0 0 14px rgba(0, 180, 255, 0.18)',
  },
  error: { ...lightStyles.error, color: '#ff6b9d' },
};

const asciiStyles = {
  container: { ...lightStyles.container, background: '#000000' },
  backRow: lightStyles.backRow,
  backButton: {
    ...lightStyles.backButton, color: '#33ff33',
    fontFamily: '"Courier New", Courier, monospace',
  },
  tabRow: {
    ...lightStyles.tabRow,
    border: '2px solid #33ff33',
    borderBottom: 'none',
    borderRadius: '0',
    fontFamily: '"Courier New", Courier, monospace',
  },
  tab: {
    ...lightStyles.tab,
    background: '#000000',
    color: 'rgba(51,255,51,0.5)',
    fontFamily: '"Courier New", Courier, monospace',
  },
  tabActive: {
    ...lightStyles.tabActive,
    background: '#000000',
    color: '#33ff33',
    borderBottom: '2px solid #33ff33',
    fontFamily: '"Courier New", Courier, monospace',
  },
  card: {
    ...lightStyles.card,
    background: '#000000',
    border: '2px solid #33ff33',
    borderTop: 'none',
    borderRadius: '0',
    boxShadow: '0 0 20px rgba(51, 255, 51, 0.2)',
    fontFamily: '"Courier New", Courier, monospace',
  },
  fieldLabel: {
    ...lightStyles.fieldLabel, color: 'rgba(51,255,51,0.6)',
    fontFamily: '"Courier New", Courier, monospace',
  },
  input: {
    ...lightStyles.input,
    background: '#000000', border: '1px solid #33ff33',
    color: '#33ff33', borderRadius: '0',
    fontFamily: '"Courier New", Courier, monospace',
  },
  gameModeRow: lightStyles.gameModeRow,
  radioGroup: lightStyles.radioGroup,
  radioLabel: {
    ...lightStyles.radioLabel, color: '#33ff33',
    fontFamily: '"Courier New", Courier, monospace',
  },
  radioInput: lightStyles.radioInput,
  toggle: { ...lightStyles.toggle, border: '1px solid #33ff33', borderRadius: '0' },
  toggleBtn: {
    ...lightStyles.toggleBtn, background: '#000000',
    color: '#33ff33', fontFamily: '"Courier New", Courier, monospace',
  },
  toggleBtnActive: {
    ...lightStyles.toggleBtnActive, background: '#33ff33',
    color: '#000000', fontFamily: '"Courier New", Courier, monospace',
  },
  actionButton: {
    ...lightStyles.actionButton,
    background: '#000000', border: '1px solid #33ff33',
    color: '#33ff33', borderRadius: '0',
    boxShadow: '0 0 10px rgba(51,255,51,0.2)',
    fontFamily: '"Courier New", Courier, monospace',
  },
  error: { ...lightStyles.error, color: '#ff6b9d' },
};

const asciiLightStyles = {
  container: { ...lightStyles.container, background: '#e8e8e4' },
  backRow: lightStyles.backRow,
  backButton: { ...lightStyles.backButton, color: '#1a1a1a', fontFamily: '"Courier New", Courier, monospace' },
  tabRow: {
    ...lightStyles.tabRow,
    border: '2px solid #444444',
    borderBottom: 'none',
    borderRadius: '0',
    fontFamily: '"Courier New", Courier, monospace',
  },
  tab: {
    ...lightStyles.tab,
    background: '#dcdcd8',
    color: '#888',
    fontFamily: '"Courier New", Courier, monospace',
  },
  tabActive: {
    ...lightStyles.tabActive,
    background: '#f2f2ee',
    color: '#1a1a1a',
    borderBottom: '2px solid #1a1a1a',
    fontFamily: '"Courier New", Courier, monospace',
  },
  card: { ...lightStyles.card, background: '#f2f2ee', border: '2px solid #444444', borderTop: 'none', borderRadius: '0', fontFamily: '"Courier New", Courier, monospace' },
  fieldLabel: { ...lightStyles.fieldLabel, color: '#555', fontFamily: '"Courier New", Courier, monospace' },
  input: { ...lightStyles.input, background: '#f8f8f6', border: '1px solid #444', color: '#1a1a1a', borderRadius: '0', fontFamily: '"Courier New", Courier, monospace' },
  gameModeRow: lightStyles.gameModeRow,
  radioGroup: lightStyles.radioGroup,
  radioLabel: { ...lightStyles.radioLabel, color: '#1a1a1a', fontFamily: '"Courier New", Courier, monospace' },
  radioInput: lightStyles.radioInput,
  toggle: { ...lightStyles.toggle, border: '1px solid #444', borderRadius: '0' },
  toggleBtn: { ...lightStyles.toggleBtn, background: '#f2f2ee', color: '#1a1a1a', fontFamily: '"Courier New", Courier, monospace' },
  toggleBtnActive: { ...lightStyles.toggleBtnActive, background: '#1a1a1a', color: '#f2f2ee', fontFamily: '"Courier New", Courier, monospace' },
  actionButton: { ...lightStyles.actionButton, background: '#1a1a1a', border: '1px solid #444', color: '#f2f2ee', borderRadius: '0', fontFamily: '"Courier New", Courier, monospace' },
  error: { ...lightStyles.error, color: '#cc0000' },
};

export default CreateRoom;
